import { supabase } from '../core/supabaseClient'
import { eventCacheService } from './cache'
import type { PostgrestFilterBuilder } from '@supabase/postgrest-js'
import type {
  EventType,
  EventPriority,
  EventSource,
  EventLevel,
  EventRow,
  EventStats
} from './types'

/**
 * 查詢篩選器
 */
interface QueryFilters {
  id?: string
  type?: EventType
  types?: EventType[]
  priority?: EventPriority
  source?: EventSource
  source_detail?: string
  event_level?: EventLevel
  organization_id?: string
  field_id?: string
  field_ids?: string[]
  device_id?: string
  system_id?: string | number
  system_include_null?: boolean
  startDate?: Date | string
  endDate?: Date | string
  createdAfter?: Date | string
  is_read?: boolean
  is_resolved?: boolean
  keyword?: string
  min_duration_minutes?: number
  includeDeleted?: boolean
}

/**
 * 排序選項
 */
interface OrderByOption {
  field: string
  ascending: boolean
}

/**
 * 緩存模式
 */
type CacheMode = 'db' | 'cache' | 'cache-first'

/**
 * 統計結果
 */
interface StatsResult {
  total: number
  unread: number
  unresolved: number
  resolved: number
  byType: Record<string, number>
  byPriority: Record<string, number>
}

/**
 * 狀態計數結果
 */
interface StatusCountsResult {
  unresolved: number
  unread: number
  resolved: number
}

// 分批查詢的 chunk 大小（避免 URL 過長）
const CHUNK_SIZE = 30

/**
 * EventQueryBuilder - 事件鏈式查詢建構器
 *
 * @example
 * // 基本查詢
 * const events = await new EventQueryBuilder().execute()
 *
 * // 鏈式篩選
 * const events = await new EventQueryBuilder()
 *   .type('alarm')
 *   .priority('high')
 *   .lastDays(7)
 *   .execute()
 *
 * // 使用緩存
 * const events = await new EventQueryBuilder()
 *   .type('alarm')
 *   .useCache()
 *   .execute()
 */
export class EventQueryBuilder {
  private _filters: QueryFilters
  private _selectFields: string
  private _orderByFields: OrderByOption[]
  private _limitValue: number | null
  private _offsetValue: number | null
  private _cacheMode: CacheMode
  private _saveToCache: boolean

  constructor() {
    this._filters = {
      min_duration_minutes: 15 // 預設過濾掉小於 15 分鐘的事件
    }
    this._selectFields = '*'
    this._orderByFields = [{ field: 'created_at', ascending: false }]
    this._limitValue = null
    this._offsetValue = null
    this._cacheMode = 'cache-first'
    this._saveToCache = false
  }

  // ========== 緩存策略 ==========

  /** 優先從緩存讀取，沒有則查 DB（預設行為） */
  useCache(): this {
    this._cacheMode = 'cache-first'
    return this
  }

  /** 強制從 DB 讀取（跳過緩存） */
  useDB(): this {
    this._cacheMode = 'db'
    return this
  }

  /** 只從緩存讀取（不查 DB） */
  cacheOnly(): this {
    this._cacheMode = 'cache'
    return this
  }

  /** 查詢結果存入緩存 */
  saveToCache(): this {
    this._saveToCache = true
    return this
  }

  // ========== 類型篩選 ==========

  /** 篩選單一事件類型 */
  type(eventType: EventType): this {
    this._filters.type = eventType
    return this
  }

  /** 篩選多種事件類型 */
  types(eventTypes: EventType[]): this {
    this._filters.types = eventTypes
    return this
  }

  /** 篩選優先級 */
  priority(level: EventPriority): this {
    this._filters.priority = level
    return this
  }

  // ========== 位置篩選 ==========

  /** 篩選單一案場 */
  field(fieldId: string): this {
    this._filters.field_id = fieldId
    return this
  }

  /** 篩選多個案場 */
  fields(fieldIds: string[]): this {
    if (fieldIds && fieldIds.length > 0) {
      this._filters.field_ids = fieldIds
    }
    return this
  }

  /** 篩選組織 */
  organization(orgId: string): this {
    this._filters.organization_id = orgId
    return this
  }

  /** 篩選設備 */
  device(deviceId: string): this {
    this._filters.device_id = deviceId
    return this
  }

  /** 篩選系統 */
  system(systemId: string | number, includeNull: boolean = true): this {
    this._filters.system_id = systemId
    this._filters.system_include_null = includeNull
    return this
  }

  /** 篩選事件層級 */
  eventLevel(level: EventLevel): this {
    this._filters.event_level = level
    return this
  }

  // ========== 日期篩選 ==========

  /** 日期範圍篩選 */
  dateRange(startDate: Date | string, endDate: Date | string): this {
    this._filters.startDate = startDate
    this._filters.endDate = endDate
    return this
  }

  /** 最近 n 天 */
  lastDays(days: number): this {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days + 1)
    startDate.setHours(0, 0, 0, 0)

    this._filters.startDate = startDate.toISOString()
    this._filters.endDate = endDate.toISOString()
    return this
  }

  /** 篩選在指定時間之後建立的事件 */
  createdAfter(date: Date | string): this {
    this._filters.createdAfter = date
    return this
  }

  // ========== 狀態篩選 ==========

  /** 只顯示未讀事件 */
  unread(): this {
    this._filters.is_read = false
    return this
  }

  /** 只顯示已讀事件 */
  read(): this {
    this._filters.is_read = true
    return this
  }

  /** 只顯示未解決事件 */
  unresolved(): this {
    this._filters.is_resolved = false
    return this
  }

  /** 只顯示已解決事件 */
  resolved(): this {
    this._filters.is_resolved = true
    return this
  }

  // ========== 搜尋篩選 ==========

  /** 關鍵字搜尋 */
  keyword(text: string): this {
    if (text && text.trim()) {
      this._filters.keyword = text.trim()
    }
    return this
  }

  /** 依 ID 篩選 */
  id(eventId: string): this {
    this._filters.id = eventId
    return this
  }

  /** 依來源篩選 */
  source(source: EventSource): this {
    this._filters.source = source
    return this
  }

  /** 依來源詳情篩選 */
  sourceDetail(sourceDetail: string): this {
    this._filters.source_detail = sourceDetail
    return this
  }

  /** 最小持續時間篩選（分鐘） */
  minDuration(minutes: number): this {
    this._filters.min_duration_minutes = minutes
    return this
  }

  // ========== 分頁與排序 ==========

  /** 限制返回數量 */
  limit(n: number): this {
    this._limitValue = n
    return this
  }

  /** 分頁偏移 */
  offset(n: number): this {
    this._offsetValue = n
    return this
  }

  /** 排序 */
  orderBy(field: string, direction: 'asc' | 'desc' = 'desc'): this {
    if (
      this._orderByFields.length === 1 &&
      this._orderByFields[0].field === 'created_at' &&
      !this._orderByFields[0].ascending
    ) {
      this._orderByFields = []
    }
    this._orderByFields.push({
      field,
      ascending: direction.toLowerCase() === 'asc'
    })
    return this
  }

  /** 選擇欄位 */
  select(fields: string[]): this {
    this._selectFields = fields.join(', ')
    return this
  }

  // ========== 執行查詢 ==========

  /** 執行查詢並返回結果 */
  async execute(): Promise<EventRow[]> {
    let result: EventRow[]

    const effectiveCacheMode = eventCacheService.isInitialized ? this._cacheMode : 'db'

    if (!eventCacheService.isInitialized && this._cacheMode !== 'db') {
      console.log('[EventService] 查詢在初始化前被呼叫，改用 DB 模式:', JSON.stringify(this._filters))
    }

    switch (effectiveCacheMode) {
      case 'cache':
        result = await this._executeFromCache()
        break

      case 'cache-first':
        const cacheHasData = await eventCacheService.hasData()
        if (cacheHasData) {
          result = await this._executeFromCache()
        } else {
          result = await this._executeFromDB()
          if (result.length > 0) {
            await eventCacheService.saveEvents(result)
          }
        }
        break

      case 'db':
      default:
        result = await this._executeFromDB()
        if (this._saveToCache && result.length > 0) {
          await eventCacheService.saveEvents(result)
        }
        break
    }

    return result
  }

  /** 從緩存執行查詢 */
  private async _executeFromCache(): Promise<EventRow[]> {
    return eventCacheService.queryEvents(this._filters, {
      orderBy: this._orderByFields,
      limit: this._limitValue,
      offset: this._offsetValue
    })
  }

  /** 從資料庫執行查詢 */
  private async _executeFromDB(): Promise<EventRow[]> {
    const f = this._filters

    if (f.field_ids && f.field_ids.length > CHUNK_SIZE) {
      return this._executeChunked()
    }

    let query = supabase.from('events').select(this._selectFields)

    query = this._applyFilters(query)

    for (const order of this._orderByFields) {
      query = query.order(order.field, { ascending: order.ascending })
    }

    if (this._limitValue !== null) {
      query = query.limit(this._limitValue)
    }
    if (this._offsetValue !== null) {
      query = query.range(this._offsetValue, this._offsetValue + (this._limitValue || 1000) - 1)
    }

    const { data, error } = await query

    if (error) {
      console.error('[EventService] 查詢失敗:', error)
      throw error
    }

    return (data || []) as EventRow[]
  }

  /** 分批執行查詢 */
  private async _executeChunked(): Promise<EventRow[]> {
    const f = this._filters
    const fieldIds = f.field_ids!
    const chunks: string[][] = []

    for (let i = 0; i < fieldIds.length; i += CHUNK_SIZE) {
      chunks.push(fieldIds.slice(i, i + CHUNK_SIZE))
    }

    console.log(`[EventService] 分批查詢: ${fieldIds.length} 個案場分成 ${chunks.length} 批`)

    const chunkPromises = chunks.map(async (chunkFieldIds) => {
      const chunkFilters = { ...f, field_ids: chunkFieldIds }

      let query = supabase.from('events').select(this._selectFields)

      query = this._applyFiltersWithOverride(query, chunkFilters)

      for (const order of this._orderByFields) {
        query = query.order(order.field, { ascending: order.ascending })
      }

      const { data, error } = await query

      if (error) {
        console.error('[EventService] chunk 查詢失敗:', error)
        throw error
      }

      return (data || []) as EventRow[]
    })

    const chunkResults = await Promise.all(chunkPromises)

    let allResults = chunkResults.flat()

    const seenIds = new Set<string>()
    allResults = allResults.filter((event) => {
      if (seenIds.has(event.id)) return false
      seenIds.add(event.id)
      return true
    })

    for (const order of this._orderByFields) {
      allResults.sort((a, b) => {
        const aVal = (a as Record<string, unknown>)[order.field]
        const bVal = (b as Record<string, unknown>)[order.field]
        if (aVal === bVal) return 0
        const comparison = aVal! < bVal! ? -1 : 1
        return order.ascending ? comparison : -comparison
      })
    }

    if (this._offsetValue !== null || this._limitValue !== null) {
      const start = this._offsetValue || 0
      const end = this._limitValue ? start + this._limitValue : undefined
      allResults = allResults.slice(start, end)
    }

    return allResults
  }

  /** 只返回數量 */
  async count(): Promise<number> {
    const f = this._filters

    if (f.field_ids && f.field_ids.length > CHUNK_SIZE) {
      return this._countChunked()
    }

    let query = supabase.from('events').select('*', { count: 'exact', head: true })

    query = this._applyFilters(query)

    const { count, error } = await query

    if (error) {
      console.error('[EventService] 計數失敗:', error)
      throw error
    }

    return count || 0
  }

  /** 分批計數 */
  private async _countChunked(): Promise<number> {
    const f = this._filters
    const fieldIds = f.field_ids!
    const chunks: string[][] = []

    for (let i = 0; i < fieldIds.length; i += CHUNK_SIZE) {
      chunks.push(fieldIds.slice(i, i + CHUNK_SIZE))
    }

    const chunkPromises = chunks.map(async (chunkFieldIds) => {
      const chunkFilters = { ...f, field_ids: chunkFieldIds }

      let query = supabase.from('events').select('*', { count: 'exact', head: true })

      query = this._applyFiltersWithOverride(query, chunkFilters)

      const { count, error } = await query

      if (error) {
        console.error('[EventService] chunk 計數失敗:', error)
        throw error
      }

      return count || 0
    })

    const counts = await Promise.all(chunkPromises)
    return counts.reduce((sum, c) => sum + c, 0)
  }

  /** 取得第一筆結果 */
  async first(): Promise<EventRow | null> {
    const results = await this.limit(1).execute()
    return results.length > 0 ? results[0] : null
  }

  /** 取得統計資料 */
  async stats(): Promise<StatsResult> {
    const f = this._filters

    if (f.field_ids && f.field_ids.length > CHUNK_SIZE) {
      return this._statsChunked()
    }

    return this._executeStats()
  }

  /** 執行統計查詢 */
  private async _executeStats(): Promise<StatsResult> {
    const baseQuery = () => {
      let query = supabase.from('events').select('*', { count: 'exact', head: true })
      return this._applyFilters(query)
    }

    const [totalResult, unreadResult, unresolvedResult, resolvedResult, byTypeResult, byPriorityResult] =
      await Promise.all([
        baseQuery().then(({ count }) => count || 0),

        (async () => {
          let q = supabase.from('events').select('*', { count: 'exact', head: true })
          q = this._applyFilters(q)
          const { count } = await q.eq('is_read', false)
          return count || 0
        })(),

        (async () => {
          let q = supabase.from('events').select('*', { count: 'exact', head: true })
          q = this._applyFilters(q)
          const { count } = await q.eq('is_resolved', false)
          return count || 0
        })(),

        (async () => {
          let q = supabase.from('events').select('*', { count: 'exact', head: true })
          q = this._applyFilters(q)
          const { count } = await q.eq('is_resolved', true)
          return count || 0
        })(),

        (async () => {
          let q = supabase.from('events').select('type')
          q = this._applyFilters(q)
          const { data } = await q
          const byType: Record<string, number> = {}
          ;(data || []).forEach((e: { type: string }) => {
            byType[e.type] = (byType[e.type] || 0) + 1
          })
          return byType
        })(),

        (async () => {
          let q = supabase.from('events').select('priority')
          q = this._applyFilters(q)
          const { data } = await q
          const byPriority: Record<string, number> = {}
          ;(data || []).forEach((e: { priority?: string }) => {
            if (e.priority) {
              byPriority[e.priority] = (byPriority[e.priority] || 0) + 1
            }
          })
          return byPriority
        })()
      ])

    return {
      total: totalResult,
      unread: unreadResult,
      unresolved: unresolvedResult,
      resolved: resolvedResult,
      byType: byTypeResult,
      byPriority: byPriorityResult
    }
  }

  /** 分批執行統計查詢 */
  private async _statsChunked(): Promise<StatsResult> {
    const f = this._filters
    const fieldIds = f.field_ids!
    const chunks: string[][] = []

    for (let i = 0; i < fieldIds.length; i += CHUNK_SIZE) {
      chunks.push(fieldIds.slice(i, i + CHUNK_SIZE))
    }

    console.log(`[EventService] 分批統計: ${fieldIds.length} 個案場分成 ${chunks.length} 批`)

    const chunkResults: StatsResult[] = []
    for (const chunkFieldIds of chunks) {
      const savedFieldIds = this._filters.field_ids
      this._filters.field_ids = chunkFieldIds
      chunkResults.push(await this._executeStats())
      this._filters.field_ids = savedFieldIds
    }

    const merged: StatsResult = {
      total: 0,
      unread: 0,
      unresolved: 0,
      resolved: 0,
      byType: {},
      byPriority: {}
    }

    chunkResults.forEach((stats) => {
      merged.total += stats.total
      merged.unread += stats.unread
      merged.unresolved += stats.unresolved
      merged.resolved += stats.resolved

      Object.entries(stats.byType).forEach(([type, count]) => {
        merged.byType[type] = (merged.byType[type] || 0) + count
      })

      Object.entries(stats.byPriority).forEach(([priority, count]) => {
        merged.byPriority[priority] = (merged.byPriority[priority] || 0) + count
      })
    })

    return merged
  }

  /** 取得簡化的狀態統計 */
  async statusCounts(): Promise<StatusCountsResult> {
    const f = this._filters

    if (f.field_ids && f.field_ids.length > CHUNK_SIZE) {
      return this._statusCountsChunked()
    }

    const buildQuery = () => {
      let q = supabase.from('events').select('*', { count: 'exact', head: true })
      return this._applyFilters(q)
    }

    const [unresolved, unread, resolved] = await Promise.all([
      buildQuery()
        .eq('is_resolved', false)
        .then(({ count }) => count || 0),
      buildQuery()
        .eq('is_read', false)
        .then(({ count }) => count || 0),
      buildQuery()
        .eq('is_resolved', true)
        .then(({ count }) => count || 0)
    ])

    return { unresolved, unread, resolved }
  }

  /** 分批取得狀態統計 */
  private async _statusCountsChunked(): Promise<StatusCountsResult> {
    const f = this._filters
    const fieldIds = f.field_ids!
    const chunks: string[][] = []

    for (let i = 0; i < fieldIds.length; i += CHUNK_SIZE) {
      chunks.push(fieldIds.slice(i, i + CHUNK_SIZE))
    }

    const chunkResults: { unresolved: number; unread: number; resolved: number }[] = []
    for (const chunkFieldIds of chunks) {
      const savedFieldIds = this._filters.field_ids
      this._filters.field_ids = chunkFieldIds

      const buildQuery = () => {
        let q = supabase.from('events').select('*', { count: 'exact', head: true })
        return this._applyFilters(q)
      }

      const [unresolved, unread, resolved] = await Promise.all([
        buildQuery()
          .eq('is_resolved', false)
          .then(({ count }) => count || 0),
        buildQuery()
          .eq('is_read', false)
          .then(({ count }) => count || 0),
        buildQuery()
          .eq('is_resolved', true)
          .then(({ count }) => count || 0)
      ])

      this._filters.field_ids = savedFieldIds
      chunkResults.push({ unresolved, unread, resolved })
    }

    return chunkResults.reduce(
      (acc, curr) => ({
        unresolved: acc.unresolved + curr.unresolved,
        unread: acc.unread + curr.unread,
        resolved: acc.resolved + curr.resolved
      }),
      { unresolved: 0, unread: 0, resolved: 0 }
    )
  }

  /** 套用篩選條件到查詢 */
  private _applyFilters<T>(query: PostgrestFilterBuilder<any, any, T>): PostgrestFilterBuilder<any, any, T> {
    return this._applyFiltersWithOverride(query, this._filters)
  }

  /** 套用篩選條件 (允許覆蓋 filters) */
  private _applyFiltersWithOverride<T>(
    query: PostgrestFilterBuilder<any, any, T>,
    filters: QueryFilters
  ): PostgrestFilterBuilder<any, any, T> {
    const f = filters

    if (!f.includeDeleted) {
      query = query.is('deleted_at', null)
    }

    if (f.id) {
      query = query.eq('id', f.id)
    }

    if (f.type) {
      query = query.eq('type', f.type)
    }
    if (f.types && f.types.length > 0) {
      query = query.in('type', f.types)
    }

    if (f.priority) {
      query = query.eq('priority', f.priority)
    }

    if (f.source) {
      query = query.eq('source', f.source)
    }
    if (f.source_detail) {
      query = query.eq('source_detail', f.source_detail)
    }

    if (f.event_level) {
      query = query.eq('event_level', f.event_level)
    }

    if (f.organization_id) {
      query = query.eq('organization_id', f.organization_id)
    }

    if (f.field_id) {
      query = query.eq('field_id', f.field_id)
    }
    if (f.field_ids && f.field_ids.length > 0) {
      query = query.in('field_id', f.field_ids)
    }

    if (f.device_id) {
      query = query.eq('device_id', f.device_id)
    }

    if (f.system_id) {
      if (f.system_include_null) {
        query = query.or(`system_id.eq.${f.system_id},system_id.is.null`)
      } else {
        query = query.eq('system_id', f.system_id)
      }
    }

    if (f.startDate) {
      query = query.gte('created_at', new Date(f.startDate).toISOString())
    }
    if (f.endDate) {
      const endDate = new Date(f.endDate)
      endDate.setHours(23, 59, 59, 999)
      query = query.lte('created_at', endDate.toISOString())
    }
    if (f.createdAfter) {
      query = query.gt('created_at', new Date(f.createdAfter).toISOString())
    }

    if (f.is_read !== undefined) {
      query = query.eq('is_read', f.is_read)
    }
    if (f.is_resolved !== undefined) {
      query = query.eq('is_resolved', f.is_resolved)
    }

    if (f.keyword) {
      const pattern = `%${f.keyword}%`
      query = query.or(
        `title.ilike.${pattern},content.ilike.${pattern},field_name.ilike.${pattern},device_name.ilike.${pattern}`
      )
    }

    if (f.min_duration_minutes && f.min_duration_minutes > 0) {
      query = query.gte('duration_minutes', f.min_duration_minutes)
    }

    return query
  }
}

export default EventQueryBuilder
